import { useCallback, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Link as LinkIcon,
  RefreshCw,
  Search,
  ShieldCheck,
  SignalHigh,
  Trash2,
} from "lucide-react";
import {
  adminApi,
  type ProcessingIncidentDetail,
  type ProcessingIncidentDetailFilters,
  type ProcessingIncidentPriority,
  type ProcessingIncidentStatus,
} from "@/services/adminApi";
import {
  SurfaceChip,
  TestBadge,
  operationLabel,
} from "@/components/support/incidentMeta";

const STATUSES: ProcessingIncidentStatus[] = [
  "OPEN",
  "TRIAGED",
  "LINKED_TO_ISSUE",
  "RESOLVED",
  "IGNORED",
];

const STATUS_META: Record<
  ProcessingIncidentStatus,
  { label: string; dot: string; chip: string }
> = {
  OPEN: { label: "Open", dot: "bg-amber-500", chip: "bg-amber-50 text-amber-700 ring-amber-200/60" },
  TRIAGED: { label: "Triaged", dot: "bg-sky-500", chip: "bg-sky-50 text-sky-700 ring-sky-200/60" },
  LINKED_TO_ISSUE: { label: "Linked", dot: "bg-violet-500", chip: "bg-violet-50 text-violet-700 ring-violet-200/60" },
  RESOLVED: { label: "Resolved", dot: "bg-emerald-500", chip: "bg-emerald-50 text-emerald-700 ring-emerald-200/60" },
  IGNORED: { label: "Ignored", dot: "bg-zinc-400", chip: "bg-zinc-50 text-zinc-600 ring-zinc-200/60" },
};

const PRIORITY_META: Record<
  ProcessingIncidentPriority,
  { label: string; chip: string; icon: typeof ShieldCheck }
> = {
  HIGH: { label: "High", chip: "bg-orange-50 text-orange-700 ring-orange-200/60", icon: ShieldCheck },
  URGENT: { label: "Urgent", chip: "bg-red-50 text-red-700 ring-red-200/70", icon: SignalHigh },
};

const emptyFilters = {
  organizationId: "",
  userId: "",
  chatId: "",
  correlationId: "",
};

export default function SupportIncidentDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<ProcessingIncidentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [draftFilters, setDraftFilters] = useState(emptyFilters);
  const [activeFilters, setActiveFilters] = useState(emptyFilters);
  const [linearForm, setLinearForm] = useState({
    externalIssueId: "",
    externalIssueIdentifier: "",
    externalIssueUrl: "",
  });

  const load = useCallback(
    async (cursor?: string | null, append = false) => {
      if (!id) return;
      if (append) setLoadingMore(true);
      else setLoading(true);
      setLoadError(null);
      try {
        const params: ProcessingIncidentDetailFilters = { take: 50 };
        if (activeFilters.organizationId) params.organizationId = activeFilters.organizationId;
        if (activeFilters.userId) params.userId = activeFilters.userId;
        if (activeFilters.chatId) params.chatId = activeFilters.chatId;
        if (activeFilters.correlationId) params.correlationId = activeFilters.correlationId;
        if (cursor) params.cursor = cursor;
        const result = await adminApi.getProcessingIncident(id, params);
        setData((current) =>
          append && current
            ? { ...result, occurrences: [...current.occurrences, ...result.occurrences] }
            : result,
        );
      } catch (e: any) {
        setLoadError(e?.response?.data?.error ?? "Failed to load processing incident");
        if (!append) setData(null);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [activeFilters, id],
  );

  useEffect(() => {
    load(null, false);
  }, [load]);

  const updateStatus = async (status: ProcessingIncidentStatus) => {
    setBusyAction("status");
    setError(null);
    try {
      await adminApi.updateProcessingIncidentStatus(id, status);
      await load(null, false);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? "Failed to update status");
    } finally {
      setBusyAction(null);
    }
  };

  const createLinear = async () => {
    setBusyAction("linear-create");
    setError(null);
    try {
      await adminApi.createProcessingIncidentLinearIssue(id);
      await load(null, false);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? "Failed to create Linear issue");
    } finally {
      setBusyAction(null);
    }
  };

  const linkLinear = async () => {
    if (
      !linearForm.externalIssueId.trim() ||
      !linearForm.externalIssueIdentifier.trim() ||
      !linearForm.externalIssueUrl.trim()
    ) {
      return;
    }
    setBusyAction("linear-link");
    setError(null);
    try {
      await adminApi.linkProcessingIncidentLinearIssue(id, {
        externalIssueId: linearForm.externalIssueId.trim(),
        externalIssueIdentifier: linearForm.externalIssueIdentifier.trim(),
        externalIssueUrl: linearForm.externalIssueUrl.trim(),
      });
      setLinearForm({
        externalIssueId: "",
        externalIssueIdentifier: "",
        externalIssueUrl: "",
      });
      await load(null, false);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? "Failed to link Linear issue");
    } finally {
      setBusyAction(null);
    }
  };

  const deleteIncident = async () => {
    setBusyAction("delete");
    setError(null);
    try {
      await adminApi.deleteProcessingIncident(id);
      navigate("/admin/support/incidents");
    } catch (e: any) {
      setError(e?.response?.data?.error ?? "Failed to delete incident");
      setConfirmingDelete(false);
      setBusyAction(null);
    }
  };

  const applyFilters = () => setActiveFilters({ ...draftFilters });
  const clearFilters = () => {
    setDraftFilters(emptyFilters);
    setActiveFilters(emptyFilters);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fafaf9]">
        <div className="mx-auto max-w-6xl px-8 py-10">
          <div className="h-6 w-32 animate-pulse rounded bg-zinc-200/70" />
          <div className="mt-8 grid gap-6 lg:grid-cols-3">
            <div className="space-y-3 lg:col-span-2">
              <div className="h-52 animate-pulse rounded-2xl bg-zinc-100" />
              <div className="h-96 animate-pulse rounded-2xl bg-zinc-100" />
            </div>
            <div className="space-y-3">
              <div className="h-52 animate-pulse rounded-2xl bg-zinc-100" />
              <div className="h-40 animate-pulse rounded-2xl bg-zinc-100" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#fafaf9] px-6">
        <div className="text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-red-50 text-red-500">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <p className="mt-3 text-[13.5px] text-zinc-600">
            {loadError ?? "Not found."}
          </p>
          <Link
            to="/admin/support/incidents"
            className="mt-4 inline-flex items-center rounded-lg border border-zinc-200/80 bg-white px-3 py-1.5 text-[12.5px] font-medium text-zinc-700 transition-colors hover:bg-zinc-50"
          >
            Back to incidents
          </Link>
        </div>
      </div>
    );
  }

  const status = STATUS_META[data.status];
  const priority = PRIORITY_META[data.priority];
  const PriorityIcon = priority.icon;
  const metadataWarnings = [data.slackNotifyError, data.urgentSlackNotifyError, data.linearSyncError, data.linearStateWarning].filter(Boolean);

  return (
    <div className="min-h-screen bg-[#fafaf9]">
      <div className="mx-auto max-w-6xl px-8 py-10">
        <Link
          to="/admin/support/incidents"
          className="group mb-6 inline-flex items-center gap-1.5 text-[12.5px] font-medium text-zinc-500 transition-colors hover:text-zinc-900"
        >
          <ChevronLeft className="h-3.5 w-3.5 transition-transform group-hover:-translate-x-0.5" />
          Back to incidents
        </Link>

        <div className="mb-8 flex items-start justify-between gap-6">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.12em] text-zinc-500">
              <span>Processing incident</span>
              <span className="text-zinc-300">·</span>
              <span className="font-mono text-[10.5px] normal-case tracking-normal text-zinc-400">
                #{data.id.slice(0, 8)}
              </span>
              {data.isTest && <TestBadge />}
            </div>
            <h1 className="mt-2 max-w-3xl break-words text-[24px] font-semibold leading-8 tracking-[-0.018em] text-zinc-900">
              {data.errorSummary}
            </h1>
            <p className="mt-1 text-[12.5px] text-zinc-500">
              First seen {formatDate(data.firstSeenAt)} · Last seen {formatDate(data.lastSeenAt)}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <SurfaceChip surface={data.incidentSurface} />
            <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[12px] font-medium ring-1 ring-inset ${priority.chip}`}>
              <PriorityIcon className="h-3.5 w-3.5" />
              {priority.label}
            </span>
            <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[12px] font-medium ring-1 ring-inset ${status.chip}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${status.dot}`} />
              {status.label}
            </span>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          <main className="space-y-4 lg:col-span-2">
            <section className="rounded-2xl border border-zinc-200/80 bg-white p-5 shadow-[0_1px_2px_rgba(0,0,0,0.02)]">
              <div className="grid gap-4 sm:grid-cols-3">
                <Metric label="Occurrences" value={data.occurrenceCount} />
                <Metric label="Affected users" value={data.affectedUserCount} />
                <Metric label="Affected orgs" value={data.affectedOrganizationCount} />
              </div>
              <div className="mt-5 grid gap-3 text-[12.5px] sm:grid-cols-2">
                <Meta label="Surface" value={data.incidentSurface} />
                <Meta label="Operation" value={operationLabel(data.incidentOperation)} />
                <Meta label="Fingerprint" value={data.fingerprint} mono small />
                <Meta label="Route" value={`${data.method} ${data.endpoint}`} mono />
                <Meta label="Source" value={data.source} />
                <Meta label="Error" value={[data.errorName, data.errorCode].filter(Boolean).join(" · ")} />
              </div>
              {data.reviewSignalAt && (
                <div className="mt-4 rounded-xl border border-orange-200/70 bg-orange-50 px-3 py-2 text-[12.5px] text-orange-800">
                  {data.reviewSignalCount} new occurrence{data.reviewSignalCount === 1 ? "" : "s"} since the last review.
                </div>
              )}
            </section>

            <section className="rounded-2xl border border-zinc-200/80 bg-white p-4 shadow-[0_1px_2px_rgba(0,0,0,0.02)]">
              <div className="mb-3 flex items-center justify-between gap-3">
                <h2 className="text-[13px] font-semibold text-zinc-900">Occurrences</h2>
                <button
                  onClick={() => load(null, false)}
                  className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[12px] font-medium text-zinc-500 transition-colors hover:bg-zinc-50 hover:text-zinc-800"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  Refresh
                </button>
              </div>
              <div className="mb-4 grid gap-2 md:grid-cols-4">
                <FilterInput
                  label="Org"
                  value={draftFilters.organizationId}
                  onChange={(organizationId) =>
                    setDraftFilters((current) => ({ ...current, organizationId }))
                  }
                />
                <FilterInput
                  label="User"
                  value={draftFilters.userId}
                  onChange={(userId) =>
                    setDraftFilters((current) => ({ ...current, userId }))
                  }
                />
                <FilterInput
                  label="Chat"
                  value={draftFilters.chatId}
                  onChange={(chatId) =>
                    setDraftFilters((current) => ({ ...current, chatId }))
                  }
                />
                <FilterInput
                  label="Correlation"
                  value={draftFilters.correlationId}
                  onChange={(correlationId) =>
                    setDraftFilters((current) => ({ ...current, correlationId }))
                  }
                />
              </div>
              <div className="mb-4 flex items-center gap-2">
                <button
                  onClick={applyFilters}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-zinc-900 px-3 py-1.5 text-[12px] font-medium text-white transition-colors hover:bg-zinc-800"
                >
                  <Search className="h-3.5 w-3.5" />
                  Apply
                </button>
                {(activeFilters.organizationId ||
                  activeFilters.userId ||
                  activeFilters.chatId ||
                  activeFilters.correlationId) && (
                  <button
                    onClick={clearFilters}
                    className="rounded-lg px-3 py-1.5 text-[12px] font-medium text-zinc-500 transition-colors hover:bg-zinc-50 hover:text-zinc-800"
                  >
                    Clear
                  </button>
                )}
              </div>
              {loadError && (
                <div className="mb-4 rounded-xl border border-red-200/70 bg-red-50 px-3 py-2 text-[12.5px] text-red-700">
                  {loadError}
                </div>
              )}

              {data.occurrences.length === 0 ? (
                <div className="rounded-xl bg-zinc-50 py-12 text-center text-[13px] text-zinc-500">
                  No occurrences match the current filters.
                </div>
              ) : (
                <ol className="space-y-3">
                  {data.occurrences.map((occurrence) => {
                    return (
                      <li key={occurrence.id} className="rounded-xl border border-zinc-200/70 bg-white p-3">
                        <div className="mb-2 flex items-start justify-between gap-3">
                          <div>
                            <p className="text-[12.5px] font-semibold text-zinc-900">
                              {formatDate(occurrence.occurredAt)}
                            </p>
                            <p className="mt-0.5 text-[11.5px] font-mono text-zinc-500">
                              {occurrence.method} {occurrence.endpoint}
                            </p>
                          </div>
                        </div>
                        <div className="grid gap-2 text-[11.5px] sm:grid-cols-2">
                          <IdentityMeta
                            label="Org"
                            primary={occurrence.organization?.name ?? occurrence.organizationId}
                            secondary={occurrence.organization?.name ? occurrence.organizationId : null}
                          />
                          <IdentityMeta
                            label="User"
                            primary={occurrence.user?.name ?? occurrence.user?.email ?? occurrence.userId}
                            secondary={occurrence.user?.name || occurrence.user?.email ? occurrence.userId : null}
                          />
                          <Meta label="Chat" value={occurrence.chatId} mono small />
                          <Meta label="Correlation" value={occurrence.correlationId} mono small />
                          <Meta label="Message" value={occurrence.messageId} mono small />
                          <Meta label="Traceparent" value={occurrence.traceparent} mono small />
                        </div>
                        {occurrence.errorDetail && (
                          <ErrorDetail value={occurrence.errorDetail} />
                        )}
                        {occurrence.stackHint && (
                          <pre className="mt-2 max-h-32 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-zinc-50 px-3 py-2 text-[11px] leading-5 text-zinc-600 ring-1 ring-inset ring-zinc-200/60">
                            {occurrence.stackHint}
                          </pre>
                        )}
                        {formatMetadata(occurrence.metadata) && (
                          <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-zinc-50 px-3 py-2 text-[11px] leading-5 text-zinc-600 ring-1 ring-inset ring-zinc-200/60">
                            {formatMetadata(occurrence.metadata)}
                          </pre>
                        )}
                      </li>
                    );
                  })}
                </ol>
              )}
              {data.occurrenceNextCursor && (
                <div className="mt-4 text-center">
                  <button
                    onClick={() => load(data.occurrenceNextCursor, true)}
                    disabled={loadingMore}
                    className="inline-flex items-center justify-center rounded-lg border border-zinc-200/80 bg-white px-4 py-2 text-[12.5px] font-medium text-zinc-700 transition-colors hover:bg-zinc-50 disabled:opacity-50"
                  >
                    {loadingMore ? "Loading…" : "Load more occurrences"}
                  </button>
                </div>
              )}
            </section>
          </main>

          <aside className="space-y-4">
            <Card title="Triage">
              <LabeledField label="Status">
                <SelectControl
                  value={data.status}
                  onChange={(status) => updateStatus(status as ProcessingIncidentStatus)}
                  disabled={busyAction === "status"}
                  options={STATUSES.map((item) => ({
                    value: item,
                    label: STATUS_META[item].label,
                  }))}
                />
              </LabeledField>
              {error && <p className="text-[11.5px] text-red-600">{error}</p>}
            </Card>

            <Card title="Linear">
              {data.externalIssueUrl ? (
                <a
                  href={data.externalIssueUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-between rounded-lg border border-violet-200/60 bg-violet-50/50 px-3 py-2.5 transition-colors hover:bg-violet-50"
                >
                  <span className="flex items-center gap-2">
                    <LinkIcon className="h-3.5 w-3.5 text-violet-700" />
                    <span className="text-[12.5px] font-medium text-violet-900">
                      {data.externalIssueIdentifier ?? "Linked"}
                    </span>
                  </span>
                  <ExternalLink className="h-3.5 w-3.5 text-violet-700/70" />
                </a>
              ) : (
                <>
                  <button
                    onClick={createLinear}
                    disabled={busyAction !== null}
                    className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-zinc-200/80 bg-white px-3 py-2 text-[12.5px] font-medium text-zinc-700 transition-all hover:border-zinc-300 hover:bg-zinc-50 disabled:opacity-50"
                  >
                    <AlertTriangle className="h-3.5 w-3.5" />
                    Create Linear issue
                  </button>
                  <div className="space-y-2 border-t border-zinc-100 pt-3">
                    <TextInput
                      label="Issue ID"
                      value={linearForm.externalIssueId}
                      onChange={(externalIssueId) =>
                        setLinearForm((current) => ({ ...current, externalIssueId }))
                      }
                    />
                    <TextInput
                      label="Identifier"
                      value={linearForm.externalIssueIdentifier}
                      placeholder="AI-123"
                      onChange={(externalIssueIdentifier) =>
                        setLinearForm((current) => ({ ...current, externalIssueIdentifier }))
                      }
                    />
                    <TextInput
                      label="URL"
                      value={linearForm.externalIssueUrl}
                      onChange={(externalIssueUrl) =>
                        setLinearForm((current) => ({ ...current, externalIssueUrl }))
                      }
                    />
                    <button
                      onClick={linkLinear}
                      disabled={busyAction !== null || !canLink(linearForm)}
                      className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-zinc-900 px-3 py-2 text-[12.5px] font-medium text-white transition-all hover:bg-zinc-800 disabled:cursor-not-allowed disabled:bg-zinc-200 disabled:text-zinc-400"
                    >
                      <LinkIcon className="h-3.5 w-3.5" />
                      Link existing issue
                    </button>
                  </div>
                </>
              )}
            </Card>

            <Card title="Affected">
              <IdentityList
                label="Users"
                items={(data.affectedUsers ?? []).map((user) => ({
                  id: user.id,
                  primary: user.name ?? user.email ?? user.id,
                  secondary: user.name || user.email ? user.id : null,
                }))}
              />
              <IdentityList
                label="Organizations"
                items={(data.affectedOrganizations ?? []).map((organization) => ({
                  id: organization.id,
                  primary: organization.name ?? organization.id,
                  secondary: organization.name ? organization.id : null,
                }))}
              />
            </Card>

            <Card title="Diagnostics">
              <Meta label="Slack notified" value={data.slackNotifiedAt ? formatDate(data.slackNotifiedAt) : null} />
              <Meta label="Urgent escalated" value={data.urgentEscalatedAt ? formatDate(data.urgentEscalatedAt) : null} />
              <Meta label="Urgent Slack" value={data.urgentSlackNotifiedAt ? formatDate(data.urgentSlackNotifiedAt) : null} />
              <Meta label="Last reviewed" value={data.lastReviewedAt ? formatDate(data.lastReviewedAt) : null} />
              {metadataWarnings.length > 0 && (
                <div className="space-y-1">
                  <p className="text-[10.5px] font-medium uppercase tracking-[0.06em] text-zinc-400">
                    Warnings
                  </p>
                  <ul className="space-y-1">
                    {metadataWarnings.map((warning, index) => (
                      <li key={index} className="rounded-lg bg-red-50 px-2 py-1.5 text-[11.5px] text-red-700 ring-1 ring-inset ring-red-100">
                        {warning}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Card>

            <Card title="Danger zone">
              {confirmingDelete ? (
                <div className="space-y-2.5">
                  <p className="text-[11.5px] leading-snug text-zinc-500">
                    Permanently delete this incident and all of its occurrences.
                    This cannot be undone. Slack and Linear alerts already sent are
                    not retracted.
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={deleteIncident}
                      disabled={busyAction !== null}
                      className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-red-600 px-3 py-2 text-[12.5px] font-medium text-white transition-all hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-red-200"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      {busyAction === "delete" ? "Deleting…" : "Confirm delete"}
                    </button>
                    <button
                      onClick={() => setConfirmingDelete(false)}
                      disabled={busyAction !== null}
                      className="rounded-lg border border-zinc-200/80 bg-white px-3 py-2 text-[12.5px] font-medium text-zinc-600 transition-colors hover:bg-zinc-50 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setConfirmingDelete(true)}
                  disabled={busyAction !== null}
                  className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-red-200/70 bg-red-50/40 px-3 py-2 text-[12.5px] font-medium text-red-700 transition-all hover:border-red-300 hover:bg-red-50 disabled:opacity-50"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete incident
                </button>
              )}
            </Card>
          </aside>
        </div>
      </div>
    </div>
  );
}

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="rounded-2xl border border-zinc-200/80 bg-white p-4 shadow-[0_1px_2px_rgba(0,0,0,0.02)]">
      <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-zinc-500">
        {title}
      </h3>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl bg-zinc-50 px-3 py-2 ring-1 ring-inset ring-zinc-200/60">
      <p className="text-[11px] font-medium uppercase tracking-[0.06em] text-zinc-400">
        {label}
      </p>
      <p className="mt-1 text-[22px] font-semibold tabular-nums tracking-tight text-zinc-900">
        {value}
      </p>
    </div>
  );
}

function LabeledField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1">
      <p className="text-[10.5px] font-medium uppercase tracking-[0.06em] text-zinc-400">
        {label}
      </p>
      {children}
    </div>
  );
}

function SelectControl({
  value,
  onChange,
  options,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  disabled?: boolean;
}) {
  return (
    <div className="relative">
      <select
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="w-full cursor-pointer appearance-none rounded-lg border border-zinc-200/80 bg-white py-1.5 pl-3 pr-8 text-[12.5px] font-medium text-zinc-800 transition-colors hover:border-zinc-300 focus:border-zinc-400 focus:outline-none disabled:cursor-not-allowed disabled:bg-zinc-50 disabled:text-zinc-400"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <ChevronRight className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 rotate-90 text-zinc-400" />
    </div>
  );
}

function Meta({
  label,
  value,
  mono,
  small,
}: {
  label: string;
  value?: string | null;
  mono?: boolean;
  small?: boolean;
}) {
  if (!value) return null;
  return (
    <div className="space-y-0.5">
      <p className="text-[10.5px] font-medium uppercase tracking-[0.06em] text-zinc-400">
        {label}
      </p>
      <p className={`break-all text-[12.5px] text-zinc-800 ${mono ? "font-mono" : ""} ${small ? "text-[11px]" : ""}`}>
        {value}
      </p>
    </div>
  );
}

function FilterInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="space-y-1">
      <span className="text-[10.5px] font-medium uppercase tracking-[0.06em] text-zinc-400">
        {label}
      </span>
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-9 w-full rounded-lg border border-zinc-200/80 bg-white px-2.5 text-[12.5px] text-zinc-800 outline-none transition-colors placeholder:text-zinc-400 focus:border-zinc-400"
      />
    </label>
  );
}

function TextInput({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="block space-y-1">
      <span className="text-[10.5px] font-medium uppercase tracking-[0.06em] text-zinc-400">
        {label}
      </span>
      <input
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        className="h-9 w-full rounded-lg border border-zinc-200/80 bg-white px-2.5 text-[12.5px] text-zinc-800 outline-none transition-colors placeholder:text-zinc-400 focus:border-zinc-400"
      />
    </label>
  );
}

function IdentityMeta({
  label,
  primary,
  secondary,
}: {
  label: string;
  primary?: string | null;
  secondary?: string | null;
}) {
  if (!primary) return null;
  return (
    <div className="space-y-0.5">
      <p className="text-[10.5px] font-medium uppercase tracking-[0.06em] text-zinc-400">
        {label}
      </p>
      <p className="break-words text-[12.5px] font-medium text-zinc-800">{primary}</p>
      {secondary && (
        <p className="break-all font-mono text-[10.5px] text-zinc-400">{secondary}</p>
      )}
    </div>
  );
}

function ErrorDetail({ value }: { value: string }) {
  return (
    <div className="mt-3 rounded-xl border border-red-100 bg-red-50/70 px-3 py-2.5">
      <p className="mb-1 text-[10.5px] font-medium uppercase tracking-[0.06em] text-red-400">
        Error message
      </p>
      <p className="whitespace-pre-wrap break-words font-mono text-[11.5px] leading-5 text-red-950">
        {value}
      </p>
    </div>
  );
}

function IdentityList({
  label,
  items,
}: {
  label: string;
  items: { id: string; primary: string; secondary?: string | null }[];
}) {
  if (!items.length) return null;
  return (
    <div className="space-y-1.5">
      <p className="text-[10.5px] font-medium uppercase tracking-[0.06em] text-zinc-400">
        {label}
      </p>
      <div className="max-h-40 space-y-1.5 overflow-auto">
        {items.map((item) => (
          <div
            key={item.id}
            className="rounded-lg bg-zinc-50 px-2.5 py-2 ring-1 ring-inset ring-zinc-200/70"
          >
            <p className="break-words text-[12px] font-medium text-zinc-800">{item.primary}</p>
            {item.secondary && (
              <p className="mt-0.5 break-all font-mono text-[10.5px] text-zinc-400">
                {item.secondary}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function canLink(form: {
  externalIssueId: string;
  externalIssueIdentifier: string;
  externalIssueUrl: string;
}) {
  return (
    form.externalIssueId.trim() &&
    form.externalIssueIdentifier.trim() &&
    form.externalIssueUrl.trim()
  );
}


function formatDate(value: string) {
  return new Date(value).toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatMetadata(value: unknown) {
  if (value == null) return null;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}
